<script setup lang="ts">
import { useI18n, useRoute, useRouter, ref, computed, onMounted, onUnmounted, useHead, watch, useRuntimeConfig, useSeoMeta, setResponseStatus, useResponseHeader } from '#imports';
import { useAuthState } from '~/composables/useAuthState';
import { useToast } from '~/composables/useToast';
import { musicService } from '~/services/musicService';
import { useMusicCreate } from '~/composables/useMusicCreate';
import { seoLandingPages } from '~/services/seoLandingConfig';
import type { SeoLandingPage } from '~/services/seoLandingConfig';
import { trackEvent } from '~/utils/analytics';
import { musicCoverUrl, musicCoverSrcset } from '~/utils/music-cover';

const { user, userAccount, handleSignIn } = useAuthState();

const toast = useToast();
const siteUrl = useRuntimeConfig().public.siteUrl || 'https://musicmint.ai';
const { saveMusicData } = useMusicCreate();

// 定义统一的音乐视图模型
interface MusicViewModel {
  id: string;
  title: string;
  image_url: string;
  audio_url: string;
  display_name: string;
  releaseDate: string;
  duration: number;
  tags: string;
  description: string;
}

type PublicMusicDetailStatus = 'available' | 'gone' | 'notFound' | 'unavailable' | 'skipped';
const SONG_SSR_TIMEOUT_MS = 2500;

interface PublicMusicDetailResult {
  status: PublicMusicDetailStatus;
  data: any | null;
}

interface LyricsSection {
  heading: string;
  lines: string[];
}

const route = useRoute();
const router = useRouter();
const musicId = computed(() => route.params.id);
const taskId = computed(() => route.query.taskId as string || '');
const { t } = useI18n();

// 状态
const isLoading = ref(true);
const music = ref<MusicViewModel | null>(null);
const coverError = ref(false);
const mobileCoverImage = ref<HTMLImageElement | null>(null);
const desktopCoverImage = ref<HTMLImageElement | null>(null);
watch(() => music.value?.image_url, () => { coverError.value = false; });
const coverSrc = computed(() => coverError.value ? music.value?.image_url || '' : musicCoverUrl(music.value?.image_url || '', 400));
const coverSrcset = computed(() => coverError.value ? undefined : musicCoverSrcset(music.value?.image_url || '', [160, 200, 320, 400, 600]));
const coverSizes = '(min-width: 1024px) 200px, 160px';
useHead(() => music.value?.image_url ? {
  link: [{ key: 'song-cover', rel: 'preload', as: 'image', href: coverSrc.value, imagesrcset: coverSrcset.value, imagesizes: coverSizes, fetchpriority: 'high' }],
} : {});
const isPlaying = ref(false);
const isUnavailable = computed(() => !isLoading.value && !music.value);
const lyricsSections = computed<LyricsSection[]>(() => {
  const rawLyrics = music.value?.description?.trim();
  if (!rawLyrics) return [];

  const sections: LyricsSection[] = [];
  let current: LyricsSection = { heading: '', lines: [] };

  const flushSection = () => {
    if (current.heading || current.lines.length > 0) {
      sections.push(current);
      current = { heading: '', lines: [] };
    }
  };

  rawLyrics
    .replace(/\r\n/g, '\n')
    .split('\n')
    .map(line => line.trim())
    .forEach((line) => {
      if (!line) {
        return;
      }

      const sectionMatch = line.match(/^\[(.+?)\]$/);
      if (sectionMatch) {
        flushSection();
        current.heading = sectionMatch[1];
        return;
      }

      current.lines.push(line);
    });

  flushSection();
  return sections;
});

const getPublicMusicDetailErrorStatus = (error: any): number | null => {
  const status = Number(error?.response?.status || error?.response?.data?.code);
  return Number.isFinite(status) ? status : null;
};

// 从Music或GenMusic转换为统一的MusicViewModel
const convertToViewModel = (data: any): MusicViewModel => {
  // 根据字段判断是GenMusic还是Music
  const isGenMusic = 'audio_oss' in data || 'stream_audio_url' in data;

  return {
    id: data.id || '',
    title: data.title || '',
    image_url: data.image_url || '',
    audio_url: isGenMusic
      ? (data.audio_oss || data.stream_audio_url || data.audio_url || '')
      : (data.audio_url || ''),
    display_name: data.display_name || t('music.unknown'),
    releaseDate: data.created_at || data.releaseDate || '',
    duration: isGenMusic
      ? (data.metadata?.duration || 0)
      : (data.metadata?.duration || data.upvote_count || 0),
    tags: data.metadata?.tags || t('music.unknown'),
    description: data.metadata?.prompt || ''
  };
};

// SSR 预取：公开歌曲（无 taskId）在服务端获取数据，确保爬虫能拿到正确 title
const { data: ssrMusicResult } = await useAsyncData<PublicMusicDetailResult>(
  `song-${route.params.id}`,
  async () => {
    // taskId 是用户私有任务数据，只在客户端获取
    if (route.query.taskId) return { status: 'skipped', data: null };
    const id = route.params.id as string;
    if (!id) return { status: 'notFound', data: null };
    try {
      const result = await musicService.getPublicMusicDetail(id, { timeout: SONG_SSR_TIMEOUT_MS });
      if (result?.code === 200 && result.data) {
        return { status: 'available', data: result.data };
      }
      if (result?.code === 410) return { status: 'gone', data: null };
      if (result?.code === 404) return { status: 'notFound', data: null };
    } catch (error: any) {
      const status = getPublicMusicDetailErrorStatus(error);
      if (status === 410) return { status: 'gone', data: null };
      if (status === 404) return { status: 'notFound', data: null };
    }
    return { status: 'unavailable', data: null };
  }
);

const unavailableResponseStatus = computed(() => {
  if (ssrMusicResult.value?.status === 'gone') return 410;
  if (ssrMusicResult.value?.status === 'notFound') return 404;
  return null;
});

if (import.meta.server && unavailableResponseStatus.value) {
  const status = unavailableResponseStatus.value;
  setResponseStatus(status, status === 410 ? 'Gone' : 'Not Found');
  useResponseHeader('X-Robots-Tag').value = 'noindex, nofollow, noarchive';
}

// 用 SSR 数据初始化，服务端渲染时 music 已有值，watch 会立即触发 updateSEO
if (ssrMusicResult.value?.status === 'available' && ssrMusicResult.value.data) {
  music.value = convertToViewModel(ssrMusicResult.value.data);
  isLoading.value = false;
} else if (!taskId.value && ssrMusicResult.value?.status !== 'unavailable') {
  isLoading.value = false;
}

useSeoMeta({
  robots: () => unavailableResponseStatus.value ? 'noindex,nofollow,noarchive' : 'index,follow',
});

useHead(() => isUnavailable.value ? {
  title: t('music.unavailableMetaTitle'),
  meta: [
    {
      name: 'description',
      content: t('music.unavailableMetaDescription'),
    },
    { property: 'og:title', content: t('music.unavailableMetaTitle') },
    {
      property: 'og:description',
      content: t('music.unavailableMetaDescription'),
    },
  ],
} : {});

// 加载音乐数据（仅用于 taskId 情况，或 SSR 未成功时的客户端兜底）
const loadMusicData = async () => {
  isLoading.value = true;
  try {
    // 如果存在taskId，则通过getMusicTaskResult获取任务
    if (taskId.value) {
      const taskResult = await musicService.getMusicTaskResult({ task_id: taskId.value });

      // 确保music_data是数组
      const musicDataArray = Array.isArray(taskResult?.data?.music_data)
        ? taskResult.data.music_data
        : (taskResult?.data?.music_data ? [taskResult.data.music_data] : []);

      if (musicDataArray.length > 0) {
        // 找到对应musicId的音乐，如果没指定则取第一个
        const targetMusic = musicId.value
          ? musicDataArray.find((item: any) => item.id === musicId.value.toString())
          : musicDataArray[0];

        if (targetMusic) {
          music.value = convertToViewModel(targetMusic);
          isLoading.value = false;
          return;
        }
      }
    } else if (musicId.value && !music.value) {
      // SSR 未成功时的客户端兜底
      const musicData = await musicService.getPublicMusicDetail(musicId.value.toString());
      if (musicData?.code === 200 && musicData.data) {
        music.value = convertToViewModel(musicData.data);
      } else {
        music.value = null;
      }
    }
  } catch (error) {
    // 加载音乐数据失败
    console.error('加载音乐数据失败:', error);
  } finally {
    isLoading.value = false;
  }
};

// 格式化时间
const formatDuration = (seconds: number | undefined) => {
  if (!seconds) return '0:00';
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
};

// 格式化日期
const formatDate = (dateString: string | undefined) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
};

// 播放音乐
const playMusic = () => {
  if (!music.value) return;

  isPlaying.value = !isPlaying.value;

  if (isPlaying.value) {
    // 创建一个包含必要播放信息的对象
    const playInfo = {
      id: music.value.id,
      title: music.value.title,
      audio_url: music.value.audio_url,
      image_url: music.value.image_url
    };

    // 触发全局播放事件
    const event = new CustomEvent('play-music', { detail: playInfo });
    window.dispatchEvent(event);
  } else {
    // 触发暂停事件
    const event = new CustomEvent('toggle-play-state', {
      detail: {
        isPlaying: false
      }
    });
    window.dispatchEvent(event);
  }
};

// 监听播放状态变化
onMounted(() => {
  const visibleCover = [mobileCoverImage.value, desktopCoverImage.value].find(image => image && image.offsetWidth > 0);
  if (music.value?.image_url && visibleCover?.complete && !visibleCover.naturalWidth) coverError.value = true;
  // 仅在 taskId 存在（私有任务数据）或 SSR 未成功获取到数据时才在客户端拉取
  if (taskId.value || (isLoading.value && !music.value)) {
    loadMusicData();
  }

  if (music.value) {
    trackEvent('song_detail_view', { source: 'song_detail', page_path: route.path, song_id: musicId.value as string, tags: music.value?.tags });
  } else {
    trackEvent('song_detail_unavailable_view', { source: 'song_detail', page_path: route.path, song_id: musicId.value as string });
  }

  // 监听播放状态变化事件
  window.addEventListener('playing-state-changed', ((e: CustomEvent) => {
    const { id, isPlaying: newIsPlaying } = e.detail;
    if (id === music.value?.id) {
      isPlaying.value = newIsPlaying;
    }
  }) as EventListener);
});

// 组件卸载时清理
onUnmounted(() => {
  window.removeEventListener('playing-state-changed', (() => { }) as EventListener);
});

// 添加SEO头部信息和Schema标记
const updateSEO = () => {
  if (!music.value) return;

  const formattedTags = music.value.tags?.split(',').map(tag => tag.trim());

  // SEO配置 - 使用新的composable
  const { setMusicSongSeo } = useSeo();
  setMusicSongSeo({
    id: music.value.id,
    title: music.value.title,
    description: music.value.description?.substring(0, 150) || t('music.onlineListen'),
    tags: formattedTags?.join(',') || music.value.tags || '',
    imageUrl: music.value.image_url,
  });

  useHead({
    script: [
      {
        type: 'application/ld+json',
        innerHTML: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "MusicComposition",
          "name": music.value.title,
          "composer": {
            "@type": "Person",
            "name": music.value.display_name
          },
          "datePublished": music.value.releaseDate,
          "description": music.value.description,
          "genre": formattedTags,
          "image": music.value.image_url,
          "url": `${siteUrl}/music/song/${music.value.id}`,
          "potentialAction": {
            "@type": "ListenAction",
            "target": {
              "@type": "EntryPoint",
              "urlTemplate": `${siteUrl}/music/song/${music.value.id}`
            }
          },
          "mainEntity": {
            "@type": "MusicRecording",
            "name": music.value.title,
            "byArtist": {
              "@type": "Person",
              "name": music.value.display_name
            },
            "duration": music.value.duration ? `PT${Math.floor(music.value.duration / 60)}M${Math.floor(music.value.duration % 60)}S` : undefined,
            "isrcCode": music.value.id,
            "recordingOf": {
              "@type": "MusicComposition",
              "name": music.value.title,
              "description": music.value.description
            }
          }
        })
      }
    ]
  });
};

// 监听音乐数据变化，更新SEO信息
watch(music, () => {
  if (music.value) {
    updateSEO();
  }
}, { immediate: true });

// 下载音乐
const handleDownloadMusic = async (music: any) => {
  if (!user.value) {
    toast.error(t('user.loginToDownloadMusic'));
    await handleSignIn();
    return;
  }
  if (userAccount.value && userAccount.value.vip_level > 0) {
    // downloadMusic(music);
    if (!music.audio_url) {
      toast.error(t('common.download_failed'));
      return;
    }
    fetch(music.audio_url).then(response => response.blob())
      .then(blob => {
        // 创建Blob URL
        const url = URL.createObjectURL(blob);

        // 创建<a>标签
        const a = document.createElement('a');
        a.href = url;
        a.download = `${music.title || 'music'}.mp3`; // 设置下载的文件名

        // 模拟点击<a>标签
        document.body.appendChild(a);
        a.click();

        // 清理
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      })
      .catch(error => console.error('下载MP3文件时出错:', error));
  } else {
    toast.error(t('user.notEnoughVip'));
    // 3秒后跳转到订阅页面
    setTimeout(() => {
      toast.info(t('create.toast.redirectingToSubscription'));
      navigateTo('/pricing');
    }, 3000);
  }
};

// Tag-to-discover page matching
const tagToDiscoverMap: Array<{ keywords: string[]; slug: string }> = [
  { keywords: ['rap', 'hip hop', 'hip-hop'], slug: 'ai-rap-song-generator' },
  { keywords: ['pop'], slug: 'ai-pop-song-generator' },
  { keywords: ['country'], slug: 'ai-country-song-generator' },
  { keywords: ['rock'], slug: 'ai-rock-song-generator' },
  { keywords: ['anime', 'jpop', 'j-pop'], slug: 'ai-anime-song-generator' },
  { keywords: ['lofi', 'lo-fi', 'chill'], slug: 'ai-lofi-music-generator' },
  { keywords: ['jazz'], slug: 'ai-jazz-music-generator' },
  { keywords: ['edm', 'electronic', 'dance'], slug: 'ai-edm-generator' },
  { keywords: ['birthday'], slug: 'ai-birthday-song-generator' },
  { keywords: ['love', 'romantic'], slug: 'ai-love-song-generator' },
  { keywords: ['funny', 'comedy'], slug: 'ai-funny-song-generator' },
];

const relatedDiscoverPages = computed(() => {
  if (!music.value?.tags) return [];
  const tagsLower = music.value.tags.toLowerCase();
  const matched: SeoLandingPage[] = [];

  for (const mapping of tagToDiscoverMap) {
    if (mapping.keywords.some(kw => tagsLower.includes(kw))) {
      const page = seoLandingPages.find(p => p.slug === mapping.slug);
      if (page) matched.push(page);
    }
    if (matched.length >= 4) break;
  }
  return matched;
});

const handleCreateSimilar = () => {
  if (!music.value) return;
  trackEvent('song_detail_create_similar_click', { source: 'song_detail', page_path: route.path, song_id: music.value.id, tags: music.value.tags });
  saveMusicData({
    prompt: music.value.description,
    tags: music.value.tags,
    title: music.value.title,
    mode: 'normal',
    source: 'song_detail',
    sourceSongId: music.value.id,
  }, route.path);
};
</script>

<template>
  <div class="music-detail-page h-full pb-4 flex flex-col overflow-x-hidden">
    <div class="mx-auto w-full max-w-6xl px-1 sm:px-4 lg:px-4 flex-1 flex flex-col h-full min-w-0">
      <!-- 返回按钮 -->
      <button @click="router.back()"
        class="flex items-center text-gray-400 hover:text-blue-500 transition-colors py-2 px-4 rounded-3xl bg-gray-800/30 hover:bg-gray-800/50 w-fit">
        <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 19l-7-7m0 0l7-7" />
        </svg>
        {{ t('music.back') }}
      </button>

      <!-- 加载状态 -->
      <div v-if="isLoading" class="flex-1 flex justify-center items-center">
        <div class="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>

      <!-- 音乐详情 -->
      <div v-else-if="music" class="flex flex-col flex-1 pt-4 min-w-0">
        <!-- 上半部分：音乐信息 -->
        <div class="backdrop-blur-sm rounded-xl mb-6 flex-shrink-0">
          <!-- 移动端：居中布局 -->
          <div class="flex flex-col items-center text-center md:hidden">
            <div class="w-40 h-40 rounded-xl overflow-hidden shadow-xl mb-4">
              <img ref="mobileCoverImage" :src="coverSrc" :srcset="coverSrcset" :sizes="coverSizes" :alt="music.title" width="160" height="160" fetchpriority="high" loading="eager" decoding="async" @error="coverError = true" class="w-full h-full object-cover">
            </div>
            <h1 class="text-xl font-bold text-white mb-2">{{ music.title }}</h1>
            <p class="text-blue-300 text-sm font-medium mb-2 line-clamp-2">{{ music.tags }}</p>
            <div class="text-sm text-gray-400 mb-3">
              <span>{{ music.display_name }}</span>
              <span class="mx-2">·</span>
              <span>{{ formatDuration(music.duration) }}</span>
            </div>
            <div class="flex items-center gap-3">
              <button @click="playMusic"
                class="px-5 py-2 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full text-white font-medium flex items-center hover:opacity-90 transition-opacity text-sm">
                <svg v-if="!isPlaying" xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 mr-1.5" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M8 5v14l11-7z" />
                </svg>
                <svg v-else xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 mr-1.5" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z" />
                </svg>
                {{ isPlaying ? t('music.pause') : t('music.play') }}
              </button>
              <button @click="handleDownloadMusic(music)"
                class="w-9 h-9 rounded-full flex items-center justify-center bg-gray-700/50 hover:bg-blue-600/50 transition-colors">
                <img src="~/assets/svg/ic-download.svg" alt="Download" class="w-5 h-5">
              </button>
            </div>
          </div>

          <!-- PC端：左右布局 -->
          <div class="hidden md:flex gap-6">
            <div class="w-[160px] lg:w-[200px] shrink-0">
              <div class="relative aspect-square rounded-xl overflow-hidden shadow-xl">
                <img ref="desktopCoverImage" :src="coverSrc" :srcset="coverSrcset" :sizes="coverSizes" :alt="music.title" width="200" height="200" fetchpriority="high" loading="eager" decoding="async" @error="coverError = true" class="w-full h-full object-cover">
                <div class="absolute bottom-2 right-2 px-2 py-1 bg-black/60 rounded-md text-white text-xs">
                  {{ formatDuration(music.duration) }}
                </div>
              </div>
            </div>
            <div class="flex-1 flex flex-col justify-center gap-2">
              <h1 class="text-2xl font-bold text-white">{{ music.title }}</h1>
              <p class="text-blue-300 text-sm font-medium line-clamp-2">{{ music.tags }}</p>
              <div class="text-sm text-white flex items-center gap-4">
                <span><span class="text-gray-400">{{ t('music.creator') }}:</span> {{ music.display_name }}</span>
                <span><span class="text-gray-400">{{ t('music.releaseDate') }}:</span> {{ music.releaseDate ? formatDate(music.releaseDate) : t('music.unknown') }}</span>
              </div>
              <div class="flex items-center gap-4 mt-2">
                <button @click="playMusic"
                  class="px-6 py-2 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full text-white font-medium flex items-center hover:opacity-90 transition-opacity">
                  <svg v-if="!isPlaying" xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 mr-2" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M8 5v14l11-7z" />
                  </svg>
                  <svg v-else xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 mr-2" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z" />
                  </svg>
                  {{ isPlaying ? t('music.pause') : t('music.play') }}
                </button>
                <button @click="handleDownloadMusic(music)"
                  class="w-10 h-10 rounded-full flex items-center justify-center bg-gray-700/50 hover:bg-blue-600/50 transition-colors">
                  <img src="~/assets/svg/ic-download.svg" alt="Download" class="w-6 h-6">
                </button>
              </div>
            </div>
          </div>
        </div>

        <!-- 下半部分：歌词 -->
        <div class="backdrop-blur-sm flex flex-col flex-1 min-h-0 lg:mb-2">
          <h2 class="text-xl font-bold text-white mb-4 flex items-center flex-shrink-0 px-1">
            <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24"
              stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            {{ t('music.lyrics') }}
          </h2>

          <div class="lyrics-stage flex-1 min-h-[280px] overflow-hidden rounded-[1.75rem] bg-slate-950/55 shadow-2xl shadow-black/20">
            <div class="lyrics-scroll h-full min-h-[280px] max-h-[58vh] overflow-y-auto px-5 py-6 sm:px-7 sm:py-7">
              <div v-if="lyricsSections.length > 0" class="mx-auto max-w-3xl">
                <section
                  v-for="(section, sectionIndex) in lyricsSections"
                  :key="`${section.heading || 'lyrics'}-${sectionIndex}`"
                  class="lyrics-section"
                >
                  <p v-if="section.heading" class="lyrics-section-title">
                    {{ section.heading }}
                  </p>
                  <div class="space-y-3 sm:space-y-3.5">
                    <p
                      v-for="(line, lineIndex) in section.lines"
                      :key="`${sectionIndex}-${lineIndex}`"
                      class="lyric-line"
                    >
                      {{ line }}
                    </p>
                  </div>
                </section>
              </div>
              <div v-else class="flex h-full min-h-[220px] items-center justify-center text-center text-gray-500">
                {{ t('music.noLyrics') }}
              </div>
            </div>
          </div>
        </div>

        <!-- Create Similar + Related Discover Links -->
        <div class="mt-6 pt-4 pb-4 border-t border-gray-700/50 shrink-0">
          <div class="flex flex-col items-center text-center" :class="relatedDiscoverPages.length > 0 ? 'mb-4' : ''">
            <h2 class="text-base font-semibold text-white mb-2">Like this song? Create something similar</h2>
            <button
              @click="handleCreateSimilar"
              class="inline-flex items-center px-5 py-2.5 rounded-full bg-gradient-to-r from-blue-500 to-purple-600 text-white text-sm font-medium hover:from-blue-600 hover:to-purple-700 transition-all shadow-lg"
            >
              <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 mr-1.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
              </svg>
              Create a Similar Song
            </button>
          </div>

          <div v-if="relatedDiscoverPages.length > 0">
            <h3 class="text-sm font-medium text-gray-400 mb-3">Related AI Music Generators</h3>
            <div class="flex flex-wrap gap-2">
              <NuxtLink
                v-for="page in relatedDiscoverPages"
                :key="page.slug"
                :to="`/discover/${page.slug}`"
                class="px-3 py-1.5 rounded-full bg-gray-800 text-gray-300 text-xs hover:bg-gray-700 hover:text-white border border-gray-700/50 hover:border-blue-500/50 transition-all"
              >
                {{ page.h1 }}
              </NuxtLink>
            </div>
          </div>
        </div>
      </div>

      <!-- 歌曲不可用承接页 -->
      <div v-else class="flex-1 flex flex-col items-center justify-center px-2 py-10 sm:px-4">
        <div class="w-full max-w-3xl text-center">
          <div class="mx-auto mb-5 flex h-16 w-16 items-center justify-center rounded-2xl bg-slate-800/80 ring-1 ring-white/10">
            <svg xmlns="http://www.w3.org/2000/svg" class="h-8 w-8 text-blue-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.8" d="M9 19V6l12-2v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2Zm12-2c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2Z" />
            </svg>
          </div>
          <p class="mb-2 text-sm font-medium uppercase tracking-[0.22em] text-blue-300/80">{{ t('music.unavailableBadge') }}</p>
          <h1 class="text-2xl font-bold text-white md:text-4xl">{{ t('music.unavailableTitle') }}</h1>
          <p class="mx-auto mt-4 max-w-2xl text-sm leading-6 text-gray-400 md:text-base">
            {{ t('music.unavailableDescription') }}
          </p>
          <div class="mt-7 flex flex-col items-center justify-center gap-3 sm:flex-row">
            <button
              @click="router.push('/music/global-trending')"
              class="inline-flex min-w-[180px] items-center justify-center rounded-full bg-gradient-to-r from-blue-500 to-purple-600 px-5 py-3 text-sm font-semibold text-white shadow-lg shadow-blue-900/20 transition hover:from-blue-600 hover:to-purple-700"
            >
              {{ t('music.exploreTrending') }}
            </button>
            <button
              @click="router.push('/create')"
              class="inline-flex min-w-[180px] items-center justify-center rounded-full border border-white/15 bg-white/5 px-5 py-3 text-sm font-semibold text-white transition hover:border-blue-300/50 hover:bg-white/10"
            >
              {{ t('music.createOwnSong') }}
            </button>
          </div>
        </div>

      </div>
    </div>
  </div>
</template>

<style scoped>
.lyrics-stage {
  position: relative;
  background:
    radial-gradient(circle at 50% 0%, rgba(74, 144, 245, 0.18), transparent 34%),
    linear-gradient(180deg, rgba(15, 23, 42, 0.84), rgba(2, 6, 23, 0.72));
}

.lyrics-stage::before,
.lyrics-stage::after {
  content: "";
  position: absolute;
  left: 0;
  right: 0;
  height: 48px;
  pointer-events: none;
  z-index: 2;
}

.lyrics-stage::before {
  top: 0;
  background: linear-gradient(180deg, rgba(2, 6, 23, 0.92), transparent);
}

.lyrics-stage::after {
  bottom: 0;
  background: linear-gradient(0deg, rgba(2, 6, 23, 0.92), transparent);
}

.lyrics-scroll {
  scrollbar-width: thin;
  scrollbar-color: rgba(96, 165, 250, 0.28) transparent;
}

.lyrics-scroll::-webkit-scrollbar {
  width: 6px;
}

.lyrics-scroll::-webkit-scrollbar-track {
  background: transparent;
}

.lyrics-scroll::-webkit-scrollbar-thumb {
  background-color: rgba(96, 165, 250, 0.28);
  border-radius: 3px;
}

.lyrics-section {
  padding: 0.35rem 0 1.5rem;
}

.lyrics-section + .lyrics-section {
  border-top: 1px solid rgba(148, 163, 184, 0.12);
  padding-top: 1.5rem;
}

.lyrics-section-title {
  margin-bottom: 0.85rem;
  color: rgba(147, 197, 253, 0.92);
  font-size: 0.78rem;
  font-weight: 700;
  letter-spacing: 0.14em;
  text-transform: uppercase;
}

.lyric-line {
  color: rgba(241, 245, 249, 0.9);
  font-size: 1.05rem;
  font-weight: 500;
  line-height: 1.85;
  overflow-wrap: anywhere;
  text-shadow: 0 1px 16px rgba(59, 130, 246, 0.12);
}

@media (max-width: 640px) {
  .lyrics-stage {
    border-radius: 1.35rem;
  }

  .lyric-line {
    font-size: 1rem;
    line-height: 1.78;
  }
}
</style>
